import classNames from 'classnames';
import { ElementType, Fragment, ReactNode } from 'react';
import { Link } from 'wouter';

import { useAuthorUrlGenerator } from '../../hooks/useUrlGenerator';

import AuthorNameTypography from '../typography/AuthorName';
import AuthorImage from './AuthorImage';

import styles from '../../styles/partials/author.module.css';

export interface AuthorProps {
    slug?: string | null;
    clickRef?: string | null;
    name: string;
    image?: Image;
    role?: string | null;
    prefix?: ReactNode;
    isPerson?: boolean;
    placeholder?: boolean;
    imageWidth?: number | null;
    imageHeight?: number | null;
    imagePosition?: 'left' | 'right';
    withoutImage?: boolean;
    withRole?: boolean;
    withBox?: boolean;
    withLink?: boolean;
    imageClassName?: string | null;
    initialsClassName?: string | null;
    labelClassName?: string | null;
    nameClassName?: string | null;
    className?: string | null;
}

function Author({
    slug = null,
    clickRef = null,
    name,
    image = null,
    role = null,
    prefix = null,
    isPerson = true,
    placeholder = false,
    imageWidth = null,
    imageHeight = null,
    imagePosition = 'left',
    withoutImage = false,
    withRole = false,
    withBox = false,
    withLink = false,
    nameClassName = null,
    imageClassName = null,
    initialsClassName = null,
    labelClassName = null,
    className = null,
}: AuthorProps) {
    const url = useAuthorUrlGenerator();

    if (!isPerson) {
        return null;
    }

    let Component: ElementType = 'div';
    if (withLink) {
        Component = Fragment;
    }

    if (withoutImage && !withRole && !withLink && prefix === null) {
        return (
            <AuthorNameTypography
                className={classNames([styles.container, className, nameClassName])}
                placeholder={placeholder}
            >
                {name}
            </AuthorNameTypography>
        );
    }

    const innerClassName = classNames([
        styles.container,
        {
            [styles.right]: imagePosition === 'right',
            [styles.withBox]: withBox,
            [styles.withoutImage]: image === null,
        },
        className,
    ]);

    const labelParts = [
        prefix !== null && (withoutImage || image === null) ? (
            <AuthorNameTypography key="prefix" className={styles.prefix}>
                {prefix}
            </AuthorNameTypography>
        ) : null,
        withRole && (role !== null || placeholder) ? (
            <AuthorNameTypography className={styles.role} placeholder={placeholder}>
                {role}
            </AuthorNameTypography>
        ) : null,

        <AuthorNameTypography
            key="name"
            className={classNames([styles.name, nameClassName])}
            placeholder={placeholder}
        >
            {name}
        </AuthorNameTypography>,
    ].filter((it) => it !== null);

    if (labelParts.length === 1 && !withLink && withoutImage && !withBox) {
        return (
            <AuthorNameTypography
                key="name"
                className={classNames([innerClassName, styles.name, nameClassName])}
                placeholder={placeholder}
            >
                {name}
            </AuthorNameTypography>
        );
    }

    const inner = (
        <Component {...(!withLink ? { className: innerClassName } : null)}>
            {prefix !== null && !withoutImage && image !== null ? (
                <AuthorNameTypography className={styles.prefix}>{prefix}</AuthorNameTypography>
            ) : null}

            {!withoutImage ? (
                <AuthorImage
                    name={name}
                    slug={slug}
                    media={image}
                    size="thumbnail"
                    width={imageWidth}
                    height={imageHeight}
                    withoutDefault
                    className={classNames([styles.image, imageClassName])}
                    initialsClassName={initialsClassName}
                />
            ) : null}
            {labelParts.length > 1 || withBox ? (
                <div className={classNames([styles.label, labelClassName])}>{labelParts}</div>
            ) : (
                labelParts
            )}
        </Component>
    );

    return withLink ? (
        <Link
            href={
                url({
                    slug,
                }) || '#'
            }
            data-click-ref={clickRef}
            className={innerClassName}
        >
            {inner}
        </Link>
    ) : (
        inner
    );
}

export default Author;
